<template>
  <div class="naver-ads-page">
    <!-- 페이지 헤더 -->
    <PageHeader 
      title="네이버 광고대상 조회" 
      description="조건에 맞는 상품의 ID를 조회하여 광고에 활용하세요." 
      :margin-bottom="true"
    />

    <!-- 툴바 -->
    <div class="toolbar-row">
      <div class="filter-item">
        <el-select v-model="filters.marketNumber" placeholder="전체" clearable style="width: 150px;">
          <el-option
            v-for="market in markets"
            :key="market.marketNumber"
            :label="`${market.marketNumber} - ${market.marketMemo}`"
            :value="market.marketNumber"
          />
        </el-select>
      </div>
      <div class="filter-item">
        <el-select
          v-model="filters.groupCode"
          placeholder="그룹코드 선택"
          clearable
          style="width: 150px;"
          @change="loadData"
          @clear="loadData"
        >
          <el-option
            v-for="opt in groupCodeOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>
      </div>
      <div class="filter-item">
        <label class="filter-label">가격:</label>
        <el-input-number v-model="filters.minPrice" :min="0" placeholder="최소" :controls="false" style="width: 120px;" />
        <span style="margin: 0 5px;">-</span>
        <el-input-number v-model="filters.maxPrice" :min="0" placeholder="최대" :controls="false" style="width: 120px;" />
      </div>
      <div class="filter-item">
        <label class="filter-label">판매:</label>
        <el-select v-model="filters.sold" clearable style="width: 120px;">
          <el-option v-for="opt in triStateOptions" :key="String(opt.value)" :label="opt.label" :value="opt.value" />
        </el-select>
      </div>
      <div class="filter-item">
        <label class="filter-label">비디오:</label>
        <el-select v-model="filters.video" clearable style="width: 120px;">
          <el-option v-for="opt in triStateOptions" :key="String(opt.value)" :label="opt.label" :value="opt.value" />
        </el-select>
      </div>
      <div class="action-buttons">
        <el-button type="primary" :icon="Search" @click="loadData" :loading="loading">조회</el-button>
        <el-button plain @click="openBanModal">광고금지</el-button>
        <el-button :icon="Refresh" @click="handleReset">초기화</el-button>
      </div>
    </div>

    <!-- 조회 결과 -->
    <div class="result-section">
      <div class="result-container">
        <div class="result-header">
          <h3 class="result-title">조회 결과 (총 {{ resultCount }}개)</h3>
          <div class="result-actions">
            <el-button 
              :icon="CopyDocument" 
              @click="copyToClipboard" 
              :disabled="!productIds.length"
            >
              클립보드에 복사
            </el-button>
            <el-button 
              :icon="Download" 
              @click="downloadAsTxt" 
              :disabled="!productIds.length"
            >
              TXT로 다운로드
            </el-button>
          </div>
        </div>
        <div class="result-content">
          <AppLoading v-if="loading" text="데이터를 불러오는 중..." />
          <el-input
            v-else
            v-model="productIdsText"
            type="textarea"
            :rows="15"
            readonly
            placeholder="조회된 상품 ID가 여기에 표시됩니다."
            class="result-textarea"
          />
        </div>
      </div>
    </div>

    <el-dialog
      v-model="banDialogVisible"
      title="광고제외 상품 추가"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form label-position="top">

        <el-form-item label="smartstoreChannelProductNo">
          <el-input
            v-model="banForm.smartstoreChannelProductNoText"
            type="textarea"
            :rows="10"
            placeholder="예)&#10;1234567890&#10;2345678901"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <div style="display:flex; gap: 8px; justify-content: flex-end;">
          <el-button @click="banDialogVisible = false" :disabled="banSubmitting">취소</el-button>
          <el-button plain @click="submitBan" :loading="banSubmitting" :disabled="banSubmitting">
            추가
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Search, Refresh, CopyDocument, Download } from '@element-plus/icons-vue';
import { getNaverAds, getReRegisterMarkets, banNaverAds } from '@/services/manager';
import { fetchGroupCodes as fetchN2nGroupCodes } from '@/services/n2n';
import AppLoading from '@/components/common/loading.vue';
import PageHeader from '@/components/common/header.vue';

export default {
  name: 'NaverAds',
  components: {
    AppLoading,
    PageHeader
  },
  setup() {
    const loading = ref(false);
    const banDialogVisible = ref(false);
    const banSubmitting = ref(false);
    const markets = ref([]);
    const groupCodes = ref([]);
    const productIds = ref([]);

    const filters = reactive({
      marketNumber: null,
      minPrice: null,
      maxPrice: null,
      groupCode: null,
      sold: null,
      video: null,
    });

    const banForm = reactive({
      option: 'basic',
      smartstoreChannelProductNoText: ''
    });

    const parseSmartstoreChannelProductNo = (text) => {
      return String(text || '')
        .split(/\r?\n/g)
        .map(v => String(v).trim())
        .filter(Boolean);
    };

    const openBanModal = () => {
      banForm.option = 'basic';
      banForm.smartstoreChannelProductNoText = '';
      banDialogVisible.value = true;
    };

    const submitBan = async () => {
      if (banSubmitting.value) return;
      const list = parseSmartstoreChannelProductNo(banForm.smartstoreChannelProductNoText);
      const unique = Array.from(new Set(list));
      if (!unique.length) {
        ElMessage.warning('smartstoreChannelProductNo를 한 줄에 하나씩 입력해주세요.');
        return;
      }
      banSubmitting.value = true;
      try {
        const res = await banNaverAds({
          smartstoreChannelProductNo: unique,
          option: banForm.option
        });
        if (res?.success) {
          const updated = res?.data?.updated;
          ElMessage.success(typeof updated === 'number' ? `${updated}건 업데이트 완료` : '광고금지 등록이 완료되었습니다.');
          banDialogVisible.value = false;
          banForm.smartstoreChannelProductNoText = '';
        } else {
          ElMessage.error(res?.message || '광고금지 등록에 실패했습니다.');
        }
      } catch (error) {
        console.error('광고금지 등록 실패:', error);
        ElMessage.error(error.response?.data?.message || error.message || '광고금지 등록 중 오류가 발생했습니다.');
      } finally {
        banSubmitting.value = false;
      }
    };

    const resultCount = computed(() => productIds.value.length);
    const productIdsText = computed(() => productIds.value.join('\n'));
    const groupCodeOptions = computed(() =>
      (groupCodes.value || []).map(group => {
        const value = group.groupid ?? group.product_group_code ?? null;
        const name = group.groupname ?? group.product_group_memo ?? '';
        const count = group.product_count;
        const labelParts = [value, name].filter(Boolean);
        const label = labelParts.join(' - ');

        return {
          value,
          label: typeof count === 'number' ? `${label} (${count}개)` : label || '-'
        };
      }).filter(option => option.value !== null)
    );
    const triStateOptions = [
      { label: '전체', value: null },
      { label: '있음', value: true },
      { label: '없음', value: false }
    ];

    const fetchMarkets = async () => {
      try {
        const response = await getReRegisterMarkets('naver');
        if (response.success) {
          markets.value = response.data;
        } else {
          ElMessage.error(response.message || '마켓 목록을 불러오는데 실패했습니다.');
        }
      } catch (error) {
        console.error('마켓 목록 조회 실패:', error);
        ElMessage.error(error.response?.data?.message || '마켓 목록 조회 중 오류가 발생했습니다.');
      }
    };

    const fetchGroupCodes = async () => {
      try {
        const { data } = await fetchN2nGroupCodes();
        groupCodes.value = data?.data ?? data ?? [];
      } catch (error) {
        groupCodes.value = [];
        console.error('그룹코드 목록 조회 실패:', error);
        ElMessage.error(error.response?.data?.message || '그룹코드 목록 조회 중 오류가 발생했습니다.');
      }
    };

    const loadData = async () => {
      loading.value = true;
      productIds.value = [];
      const params = {};

      if (filters.marketNumber !== null) params.marketNumber = filters.marketNumber;
      if (filters.groupCode !== null && filters.groupCode !== '') params.groupCode = filters.groupCode;
      if (filters.minPrice !== null) params.minPrice = filters.minPrice;
      if (filters.maxPrice !== null) params.maxPrice = filters.maxPrice;
      if (filters.sold !== null) params.sold = filters.sold;
      if (filters.video !== null) params.video = filters.video;

      try {
        const response = await getNaverAds(params);
        if (response.success) {
          productIds.value = response.data.items || [];
          ElMessage.success(`총 ${response.data.count}개의 상품 ID를 조회했습니다.`);
        } else {
          ElMessage.error(response.message || '데이터를 불러오는데 실패했습니다.');
        }
      } catch (error) {
        console.error('광고 대상 조회 실패:', error);
        ElMessage.error(error.response?.data?.message || '광고 대상 조회 중 오류가 발생했습니다.');
      } finally {
        loading.value = false;
      }
    };

    const handleReset = () => {
      filters.marketNumber = null;
      filters.minPrice = null;
      filters.maxPrice = null;
      filters.groupCode = null;
      filters.sold = null;
      filters.video = null;
      productIds.value = [];
      fetchGroupCodes();
    };
    
    const copyToClipboard = () => {
      if (!productIdsText.value) {
        ElMessage.warning('복사할 내용이 없습니다.');
        return;
      }
      navigator.clipboard.writeText(productIdsText.value)
        .then(() => {
          ElMessage.success('클립보드에 복사되었습니다.');
        })
        .catch(err => {
          console.error('복사 실패:', err);
          ElMessage.error('클립보드 복사에 실패했습니다.');
        });
    };

    const downloadAsTxt = () => {
      if (!productIdsText.value) {
        ElMessage.warning('다운로드할 내용이 없습니다.');
        return;
      }
      const blob = new Blob([productIdsText.value], { type: 'text/plain;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'naver_ads_product_ids.txt';
      link.click();
      URL.revokeObjectURL(link.href);
    };

    onMounted(() => {
      fetchMarkets();
      fetchGroupCodes();
    });

    return {
      loading,
      filters,
      banDialogVisible,
      banSubmitting,
      banForm,
      markets,
      groupCodes,
      groupCodeOptions,
      triStateOptions,
      productIds,
      resultCount,
      productIdsText,
      loadData,
      openBanModal,
      submitBan,
      handleReset,
      copyToClipboard,
      downloadAsTxt,
      Search,
      Refresh,
      CopyDocument,
      Download
    };
  }
}
</script>

<style scoped>
.naver-ads-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--el-bg-color-page);
}

/* 툴바 */
.toolbar-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
  background: var(--el-bg-color);
  border-radius: var(--el-border-radius-base);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--el-border-color-lighter);
  margin-bottom: var(--spacing-xs);
  flex-shrink: 0;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.filter-label {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
  white-space: nowrap;
}

.action-buttons {
  display: flex;
  gap: var(--spacing-sm);
  margin-left: auto;
}

.result-section {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.result-container {
  height: 100%;
  background: var(--el-bg-color);
  border-radius: var(--el-border-radius-base);
  border: 1px solid var(--el-border-color-lighter);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: var(--spacing-md);
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-md);
  flex-shrink: 0;
}

.result-title {
  font-size: var(--el-font-size-large);
  font-weight: var(--el-font-weight-bold);
  color: var(--el-text-color-primary);
  margin: 0;
}

.result-actions {
  display: flex;
  gap: var(--spacing-sm);
}

.result-content {
  flex: 1;
  overflow: auto;
  position: relative;
}

.result-textarea {
  height: 100%;
  font-family: monospace;
}

:deep(.el-textarea__inner) {
  height: 100% !important;
  resize: none;
}
</style>
